import { createContext, type ReactElement, useCallback, useContext, useMemo, useState } from 'react'

interface ListViewProviderProps {
  children: ReactElement
}

export type FilterValues = Record<string, unknown>

interface ListViewContextValue<T extends FilterValues> {
  filters: T
  filtersDirty: boolean
  setFilters: (filters: Partial<T>) => void
  resetFilters: () => void
  page: number
  setPage: (page: number) => void
  saveScrollPosition: (newScrollPosition?: number) => void
  restoreScrollPosition: () => void
}

/**
 * Creates a typed provider/hook pair that resets pagination when filters change,
 * and includes values from an optional context extension.
 */
const createListViewContext = <T extends FilterValues, E extends object = {}>(
  defaultFilters: T,
  useContextExtension: () => E = () => ({} as E), // defaults to empty hook
) => {
  const ListViewContext = createContext<(ListViewContextValue<T> & E) | null>(null)

  const ListViewProvider = ({ children }: ListViewProviderProps): ReactElement => {
    const contextExtension = useContextExtension()
    const [filters, setFilters] = useState<T>(defaultFilters)
    const [page, setPage] = useState(0)
    const filtersDirty = JSON.stringify(filters) !== JSON.stringify(defaultFilters)
    const [scrollPosition, setScrollPosition] = useState<number | null>(null)

    const setFiltersAction = useCallback((nextFilters: Partial<T>) => {
      setFilters(prev => ({
        ...prev,
        ...nextFilters,
      }))
      setPage(0)
    }, [])

    const resetFiltersAction = useCallback(() => {
      setFilters(defaultFilters)
      setPage(0)
    }, [defaultFilters])

    const setPageAction = useCallback((page: number) => {
      setScrollPosition(0)
      setPage(page)
    }, [])

    const saveScrollPosition = useCallback((newScrollPosition?: number) => {
      setScrollPosition(newScrollPosition ?? window.scrollY)
    }, [])

    const restoreScrollPosition = useCallback(() => {
      if (scrollPosition !== null) {
        setTimeout(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'instant' })
          setScrollPosition(null)
        }, 100)
      }
    }, [scrollPosition])

    const value = useMemo<ListViewContextValue<T> & E>(() => ({
      filters,
      filtersDirty,
      setFilters: setFiltersAction,
      resetFilters: resetFiltersAction,
      page,
      setPage: setPageAction,
      saveScrollPosition,
      restoreScrollPosition,
      ...contextExtension,
    }), [filters, filtersDirty, setFiltersAction, resetFiltersAction, page, setPageAction, saveScrollPosition, restoreScrollPosition, contextExtension])

    return (
      <ListViewContext.Provider value={value}>
        {children}
      </ListViewContext.Provider>
    )
  }

  function useListView() {
    const context = useContext(ListViewContext)
    if (!context) {
      throw new Error('useListView must be used within ListViewProvider')
    }
    return context
  }

  return {
    ListViewProvider,
    useListView,
  }
}

export default createListViewContext
